import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { networkService } from '../../services/api';
import '../../styles/Admin.css';
import '../../styles/NetworkManagement.css';

const NetworkManagement = () => {
  const { currentUser } = useAuth();
  const [networks, setNetworks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  
  // État pour le formulaire d'ajout/modification
  const [isEditing, setIsEditing] = useState(false);
  const [currentNetwork, setCurrentNetwork] = useState({ name: '', description: '', active: true });
  
  // Vérifier si l'utilisateur est administrateur au chargement du composant
  useEffect(() => {
    console.log('Informations utilisateur:', currentUser);
    if (currentUser && currentUser.isAdmin) {
      console.log('Utilisateur administrateur détecté');
      setIsAdmin(true);
    } else {
      console.log('Utilisateur non administrateur ou information manquante');
      setIsAdmin(false);
    }
  }, [currentUser]);

  // Charger les réseaux au chargement de la page
  useEffect(() => {
    loadNetworks();
  }, []);
  
  // Fonction pour charger les réseaux
  const loadNetworks = async () => {
    try {
      setLoading(true);
      setError(null);
      // Inclure les réseaux inactifs pour les administrateurs
      const data = await networkService.getNetworks(true);
      setNetworks(data);
    } catch (err) {
      setError('Erreur lors du chargement des réseaux: ' + (err.message || 'Erreur inconnue'));
      console.error('Erreur:', err);
    } finally {
      setLoading(false);
    }
  };
  
  // Gérer la soumission du formulaire
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Vérifier si l'utilisateur est administrateur
    if (!isAdmin) {
      setError("Vous n'avez pas les droits d'administrateur nécessaires pour effectuer cette action.");
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);
      
      if (isEditing && currentNetwork._id) {
        // Mise à jour d'un réseau existant
        await networkService.updateNetwork(currentNetwork._id, currentNetwork);
        setSuccess('Réseau mis à jour avec succès');
      } else {
        // Création d'un nouveau réseau
        await networkService.createNetwork(currentNetwork);
        setSuccess('Réseau créé avec succès');
      }
      
      // Réinitialiser le formulaire et recharger les réseaux
      resetForm();
      loadNetworks();
    } catch (err) {
      setError('Erreur lors de l\'enregistrement du réseau: ' + (err.message || 'Erreur inconnue'));
      console.error('Erreur:', err);
    } finally {
      setLoading(false);
    }
  };
  
  // Gérer la modification d'un réseau
  const handleEdit = (network) => {
    setCurrentNetwork(network);
    setIsEditing(true);
  };
  
  // Gérer la suppression d'un réseau
  const handleDelete = async (id) => {
    // Vérifier si l'utilisateur est administrateur
    if (!isAdmin) {
      setError("Vous n'avez pas les droits d'administrateur nécessaires pour effectuer cette action.");
      return;
    }
    
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce réseau ?')) {
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);
      
      await networkService.deleteNetwork(id);
      setSuccess('Réseau supprimé avec succès');
      
      // Recharger les réseaux
      loadNetworks();
    } catch (err) {
      setError('Erreur lors de la suppression du réseau: ' + (err.message || 'Erreur inconnue'));
      console.error('Erreur:', err);
    } finally {
      setLoading(false);
    }
  };
  
  // Gérer le changement de statut (actif/inactif)
  const handleToggleStatus = async (network) => {
    // Vérifier si l'utilisateur est administrateur
    if (!isAdmin) {
      setError("Vous n'avez pas les droits d'administrateur nécessaires pour effectuer cette action.");
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);
      
      const updatedNetwork = { ...network, active: !network.active };
      await networkService.updateNetwork(network._id, updatedNetwork);
      setSuccess(`Réseau ${updatedNetwork.active ? 'activé' : 'désactivé'} avec succès`);
      
      // Recharger les réseaux
      loadNetworks();
    } catch (err) {
      setError('Erreur lors de la modification du statut: ' + (err.message || 'Erreur inconnue'));
      console.error('Erreur:', err);
    } finally {
      setLoading(false);
    }
  };
  
  // Réinitialiser le formulaire
  const resetForm = () => {
    setCurrentNetwork({ name: '', description: '', active: true });
    setIsEditing(false);
  };
  
  // Gérer les changements dans le formulaire
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setCurrentNetwork({
      ...currentNetwork,
      [name]: type === 'checkbox' ? checked : value
    });
  };
  
  return (
    <div className="admin-container">
      <h1 className="page-title">Gestion des réseaux</h1>
      
      {error && <div className="alert alert-danger">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}
      
      {!currentUser && (
        <div className="alert alert-warning">
          Vous devez être connecté pour accéder à cette page.
        </div>
      )}
      
      {currentUser && !isAdmin && (
        <div className="alert alert-warning">
          Vous n'avez pas les droits d'administrateur nécessaires pour gérer les réseaux.
          Seuls les administrateurs peuvent créer, modifier ou supprimer des réseaux.
        </div>
      )}
      
      {currentUser && isAdmin && (
        <div className="admin-content-grid">
        <div className="admin-form-container">
          <h2 className="section-title">{isEditing ? 'Modifier un réseau' : 'Ajouter un réseau'}</h2>
          <form onSubmit={handleSubmit} className="admin-form">
            <div className="form-group">
              <label htmlFor="name">Nom du réseau</label>
              <input
                type="text"
                id="name"
                name="name"
                value={currentNetwork.name}
                onChange={handleChange}
                required
                className="form-control"
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="description">Description</label>
              <textarea
                id="description"
                name="description"
                value={currentNetwork.description || ''}
                onChange={handleChange}
                className="form-control"
                rows="3"
              />
            </div>
            
            <div className="form-group checkbox-group">
              <input
                type="checkbox"
                id="active"
                name="active"
                checked={currentNetwork.active}
                onChange={handleChange}
                className="form-checkbox"
              />
              <label htmlFor="active">Actif</label>
            </div>
            
            <div className="form-actions">
              <button type="submit" className="btn btn-primary" disabled={loading}>
                {isEditing ? 'Mettre à jour' : 'Ajouter'}
              </button>
              {isEditing && (
                <button type="button" className="btn btn-secondary" onClick={resetForm}>
                  Annuler
                </button>
              )}
            </div>
          </form>
        </div>
        
        <div className="admin-list-container">
          <h2>Liste des réseaux</h2>
          {loading && <div className="loading">Chargement...</div>}
          
          {networks.length === 0 ? (
            <div className="empty-message">Aucun réseau trouvé</div>
          ) : (
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Nom</th>
                  <th>Description</th>
                  <th>Statut</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {networks.map((network) => (
                  <tr key={network._id} className={!network.active ? 'inactive-row' : ''}>
                    <td>{network.name}</td>
                    <td>{network.description || '-'}</td>
                    <td>
                      <span className={`status-badge ${network.active ? 'active' : 'inactive'}`}>
                        {network.active ? 'Actif' : 'Inactif'}
                      </span>
                    </td>
                    <td className="actions-cell">
                      <button
                        onClick={() => handleEdit(network)}
                        className="btn-icon"
                        title="Modifier"
                      >
                        <i className="fas fa-edit"></i>
                      </button>
                      <button
                        onClick={() => handleToggleStatus(network)}
                        className="btn-icon"
                        title={network.active ? 'Désactiver' : 'Activer'}
                      >
                        <i className={`fas fa-${network.active ? 'times' : 'check'}`}></i>
                      </button>
                      <button
                        onClick={() => handleDelete(network._id)}
                        className="btn-icon btn-danger"
                        title="Supprimer"
                      >
                        <i className="fas fa-trash"></i>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
      )}
    </div>
  );
};

export default NetworkManagement;
